import cachedHttpClient from '../cached-http-client';
import { ApiResponse } from '../../types/responses/api-response';
import { ParticipantStatus, ExportFormat, DateRangePeriod } from '../../types/analytics';

export interface ParticipantAnalytics {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  registrationDate: string;
  eventsAttended: number;
  totalSpent: number;
  averageRating: number;
  location: string;
  status: ParticipantStatus;
  lastActivity: string;
  // Дополнительные поля для аналитики
  eventsRegistered: number;
  eventsCompleted: number;
  averageEventRating: number;
  referralCount: number;
  totalEarnings: number;
  membershipDuration: number;
  engagementScore: number;
}

export interface ParticipantMetrics {
  totalParticipants: number;
  newParticipants: number;
  activeParticipants: number;
  vipParticipants: number;
  averageEventsPerParticipant: number;
  totalRevenue: number;
  retentionRate: number;
}

export interface ParticipantsFilters {
  dateRange?: DateRangePeriod;
  status?: ParticipantStatus;
  location?: string;
  page?: number;
  limit?: number;
}

export const participantsAnalyticsApi = {
  // Получение списка участников с аналитикой
  getParticipants: async (filters: ParticipantsFilters = {}): Promise<ApiResponse<{
    participants: ParticipantAnalytics[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    filters: {
      dateRange: string;
      status: string;
      location: string;
    };
  }>> => {
    return cachedHttpClient.get('/analytics/participants', { params: filters });
  },

  // Получение метрик участников
  getParticipantsMetrics: async (filters: ParticipantsFilters = {}): Promise<ApiResponse<{
    overview: {
      totalParticipants: number;
      newParticipants: number;
      activeParticipants: number;
      vipParticipants: number;
      inactiveParticipants: number;
    };
    engagement: {
      averageEventsPerParticipant: number;
      averageSpendingPerParticipant: number;
      averageRating: number;
      retentionRate: number;
      churnRate: number;
    };
    financial: {
      totalRevenue: number;
      averageRevenuePerParticipant: number;
      topSpenders: number;
      revenueGrowth: number;
    };
    activity: {
      averageSessionDuration: number;
      averageLoginFrequency: number;
      mostActiveTime: string;
      mostActiveDay: string;
    };
    trends: {
      registrationTrend: Array<{ date: string; count: number }>;
      activityTrend: Array<{ date: string; activeUsers: number }>;
      revenueTrend: Array<{ date: string; revenue: number }>;
    };
  }>> => {
    return cachedHttpClient.get('/analytics/participants/metrics', { params: filters });
  },

  // Получение участников по событию
  getEventParticipants: async (eventId: string, filters: ParticipantsFilters = {}): Promise<ApiResponse<ParticipantAnalytics[]>> => {
    return cachedHttpClient.get(`/analytics/events/${eventId}/participants`, { params: filters });
  },

  // Получение сегментации участников
  getParticipantsSegmentation: async (filters: ParticipantsFilters & { groupBy?: 'age' | 'location' | 'activity' | 'spending' | 'engagement' } = {}): Promise<ApiResponse<{
    byAge: Array<{ ageGroup: string; count: number; percentage: number }>;
    byLocation: Array<{ location: string; count: number; percentage: number }>;
    byActivity: Array<{ activityLevel: string; count: number; percentage: number }>;
    bySpending: Array<{ spendingLevel: string; count: number; percentage: number }>;
    byEngagement: Array<{ engagementLevel: string; count: number; percentage: number }>;
  }>> => {
    return cachedHttpClient.get('/analytics/participants/segmentation', { params: filters });
  },

  // Экспорт данных участников
  exportParticipants: async (format: ExportFormat, filters: ParticipantsFilters = {}): Promise<ApiResponse<{
    downloadUrl: string;
    filename: string;
    expiresAt: string;
    fileSize: number;
  }>> => {
    return cachedHttpClient.get(`/analytics/participants/export`, { params: { ...filters, format } });
  }
};

export default participantsAnalyticsApi;
